import { useMemo, useState } from "react";
import { getAccountChargesByUser, useQuery } from "wasp/client/operations";
import { Pencil } from "lucide-react";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { Badge } from "../../../client/components/ui/badge";
import { usePfmData } from "../../../prop-firm-manager/PfmDataContext";
import { CURRENCY } from "../../../shared/currency";
import { isActivePropFirmAccount } from "../../../prop-firm-manager/pfmPeriod";
import AccountFinanceDialog from "../../../prop-firm-manager/AccountFinanceDialog";
import AccountExpenseDeleteButton from "../../../prop-firm-manager/AccountExpenseDeleteButton";

function fmt(amount: number) {
  return new Intl.NumberFormat(undefined, {
    style: "currency", currency: CURRENCY, minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount);
}

function StatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    pa: "border-primary/40 text-primary",
    challenge: "border-amber-400/40 text-amber-400",
    demo: "border-blue-400/40 text-blue-400",
    live: "border-blue-500/40 text-blue-500",
    inactive: "border-gray-400/40 text-gray-400",
  };
  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${colorMap[status] ?? "border-gray-400/40 text-gray-400"}`}>
      {status}
    </Badge>
  );
}

export default function PropFirmAccountsWidget() {
  const { accounts, isDark, t } = usePfmData();
  const { data: chargesData, refetch: refetchCharges } = useQuery(getAccountChargesByUser);
  const charges = (chargesData ?? []) as any[];
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);

  const propFirmAccounts = useMemo(() => {
    return (accounts ?? []).filter(
      (a: any) => isActivePropFirmAccount(a) || (a.status === "inactive" && (a.payouts ?? []).length > 0),
    );
  }, [accounts]);

  // Relu dans `accounts` à chaque rendu : un payout supprimé depuis la modale
  // disparaît aussitôt de sa liste.
  const editingAccount = editingAccountId
    ? propFirmAccounts.find((a: any) => a.id === editingAccountId) ?? null
    : null;

  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const border = isDark ? "border-border" : "border-gray-200";

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader label={t.analytics.payoutsPerAccount} isDark={isDark} compact />
      <CardContent className="px-4 pb-4 pt-3 flex-1 overflow-auto">
        {propFirmAccounts.length === 0 ? (
          <div className={`text-sm ${textSecondary} py-4 text-center`}>{t.analytics.payoutsNoData}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={`border-b ${border}`}>
                  <th className={`text-left pb-2 pr-3 font-medium ${textSecondary}`}>{t.analytics.payoutsAccountName}</th>
                  <th className={`text-left pb-2 pr-3 font-medium ${textSecondary}`}>{t.dashboard.accountsTableStatus}</th>
                  <th className={`text-right pb-2 pr-3 font-medium ${textSecondary}`}>{t.analytics.payoutsChallengeCost}</th>
                  <th className={`text-right pb-2 pr-2 font-medium ${textSecondary}`}>{t.analytics.payoutsReceived}</th>
                  <th className="pb-2 w-14"><span className="sr-only">{t.analytics.payoutsEditAction}</span></th>
                </tr>
              </thead>
              <tbody>
                {propFirmAccounts.map((account: any) => {
                  const received = ((account.payouts ?? []) as { amount: number }[]).reduce((s, p) => s + p.amount, 0);
                  const challengeCost = account.challengeCost ?? 0;
                  return (
                    <tr key={account.id} className={`border-b ${border} last:border-b-0`}>
                      <td className="py-2 pr-3 font-medium max-w-[120px] truncate" title={account.name}>{account.name}</td>
                      <td className="py-2 pr-3"><StatusBadge status={account.status} /></td>
                      <td className="py-2 pr-3 text-right font-mono">
                        {challengeCost > 0 ? <span className="text-red-500">-{fmt(challengeCost)}</span> : <span className={textSecondary}>—</span>}
                      </td>
                      <td className="py-2 pr-2 text-right font-mono font-semibold">
                        {received > 0 ? <span className="text-primary">+{fmt(received)}</span> : <span className={textSecondary}>—</span>}
                      </td>
                      <td className="py-2 text-right">
                        <div className="inline-flex items-center justify-end gap-0.5">
                          <button
                            type="button"
                            onClick={() => setEditingAccountId(account.id)}
                            className={`inline-flex h-6 w-6 items-center justify-center rounded transition-colors ${isDark ? "text-gray-400 hover:bg-gray-700 hover:text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"}`}
                            aria-label={`${t.analytics.payoutsEditAction} (${account.name})`}
                            title={t.analytics.payoutsEditAction}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <AccountExpenseDeleteButton account={account} isDark={isDark} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <AccountFinanceDialog
        account={editingAccount}
        charges={editingAccount ? charges.filter((c) => c.accountId === editingAccount.id) : []}
        onClose={() => setEditingAccountId(null)}
        onChargesMutated={() => { refetchCharges(); }}
      />
    </Card>
  );
}
